import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ContentTypeChip } from "./ContentTypeChip";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import { BlockTypes } from "../../models/block-types";
import { blockConfigs, flattenIOPS } from "../../models/block";
import { blockDescriptions } from "../../models/block-descriptions";
import { getCompactNumberStr } from "../../utils/iops-utils";

interface Props {
  blockType: BlockTypes;
  open: boolean;
  onClose: () => void;
}

export const BlockInfoModal: React.FC<Props> = ({ blockType, open, onClose }) => {
  const config = blockConfigs[blockType];
  const description = blockDescriptions[blockType];

  if (!config || !description) return null;

  const flatInput = flattenIOPS(config.inputCapacity);
  const flatProcessing = config.processingCapacity
    ? flattenIOPS(config.processingCapacity)
    : null;

  const inputTypes = Array.from(config.allowedInputTypes || []).map(
    (t) => blockConfigs[t]?.displayName || t
  );
  const outputTypes = Array.from(config.allowedOutputTypes || []).map(
    (t) => blockConfigs[t]?.displayName || t
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h6" component="span" fontWeight={700}>
            {config.displayName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {description.summary}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* What It Is */}
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          What It Is
        </Typography>
        <Typography variant="body2" paragraph>
          {description.whatItIs}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Used For */}
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Common Use Cases
        </Typography>
        <List dense disablePadding>
          {description.usedFor.map((use, i) => (
            <ListItem key={i} disableGutters>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <CheckCircleOutlineIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText primary={use} primaryTypographyProps={{ variant: "body2" }} />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        {/* TPS Capacities */}
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          TPS Capacities
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
          <Chip
            label={`Input: ${getCompactNumberStr(flatInput.total)} total`}
            size="small"
            color="primary"
            variant="outlined"
          />
          {flatProcessing && flatProcessing.total > 0 && (
            <Chip
              label={`Processing: ${getCompactNumberStr(flatProcessing.total)} total`}
              size="small"
              color="secondary"
              variant="outlined"
            />
          )}
        </Box>
        <Box sx={{ pl: 1, display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          {Object.keys(flatInput)
            .filter((k) => k !== "total")
            .map((key) => (
              <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <ContentTypeChip typeKey={key} />
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  {getCompactNumberStr(flatInput[key])} TPS
                </Typography>
              </Box>
            ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Connectivity */}
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Connectivity
        </Typography>
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" fontWeight={600}>
            Accepts input from:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
            {inputTypes.length > 0 ? (
              inputTypes.map((name) => (
                <Chip key={name} label={name} size="small" variant="outlined" />
              ))
            ) : (
              <Typography variant="caption" color="text.secondary">
                None (root node)
              </Typography>
            )}
          </Box>
        </Box>
        <Box>
          <Typography variant="caption" fontWeight={600}>
            Can output to:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
            {outputTypes.length > 0 ? (
              outputTypes.map((name) => (
                <Chip key={name} label={name} size="small" variant="outlined" />
              ))
            ) : (
              <Typography variant="caption" color="text.secondary">
                None (terminal node — data ends here)
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Best At */}
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Best At
        </Typography>
        <List dense disablePadding>
          {description.bestAt.map((item, i) => (
            <ListItem key={i} disableGutters>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <StarOutlineIcon fontSize="small" color="warning" />
              </ListItemIcon>
              <ListItemText primary={item} primaryTypographyProps={{ variant: "body2" }} />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        {/* Tips */}
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Tips & Insights
        </Typography>
        <List dense disablePadding>
          {description.tips.map((tip, i) => (
            <ListItem key={i} disableGutters>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <LightbulbOutlinedIcon fontSize="small" color="info" />
              </ListItemIcon>
              <ListItemText primary={tip} primaryTypographyProps={{ variant: "body2" }} />
            </ListItem>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
};
